import { card } from "../../design-system/components/card.ts";
import { notice } from "../../design-system/components/feedback.ts";
import { icon } from "../../design-system/components/icon.ts";
import { el } from "../../design-system/dom.ts";
import { ClaimFlow } from "../receiver/claim-experience.ts";
import { claimSurface } from "../receiver/claim-surface.ts";
import {
  type ReceiverScenario,
  SyntheticClaimProvider,
} from "../receiver/synthetic-claim-provider.ts";
import type { Navigate, RouteView } from "../router.ts";
import type { ShellState } from "../state.ts";
import { tenantById } from "../tenants.ts";

/**
 * Direct-entry demo links, for testing the journey a link arrives through.
 *
 * ## What this is for, exactly
 *
 * The receiver experience is reached, in the world it is designed for, by
 * tapping a link in a messaging app. Everything validated so far was reached by
 * pressing a control on an internal page, which tests the screens but not the
 * arrival: an in-app browser, a cold start, a viewport that opens under a
 * keyboard-less Safari chrome, no prior state. This gives the product owner a
 * URL they can paste into a chat and tap on a phone.
 *
 * ## What changed about hosting neutrality, stated plainly
 *
 * Until now the claimant surface had **no route at all**, and that was one of
 * the properties this milestone series claimed. It is now reachable at five
 * named paths, and the honest statement is narrower than before:
 *
 * > The claimant surface is reachable only at explicitly-named demo paths, none
 * > of which can carry an identifier, and no production hosting topology is
 * > selected.
 *
 * That remains true and remains enforced, but it is a smaller claim than "not
 * routable", so `docs/roadmap/PHASE_03_CLAIM_EXPERIENCE_SCOPE.md` records the
 * change rather than letting the old sentence quietly become false.
 *
 * ## Why these paths cannot be mistaken for the production contract
 *
 * The normative claim URL is `https://<host>/c/<ref>#<secret>` — a short path
 * segment carrying an opaque reference, and a fragment carrying a bearer
 * secret (ADR-0002; PV-16 is validating its capture and cleanup). These paths
 * share none of that shape:
 *
 * - the namespace is literally `/receiver-demo/`;
 * - each path is a **fixed literal registered in the route table**, one per
 *   scenario, so there is no parameter, no wildcard and no segment that is ever
 *   read as data. Nothing here parses a path;
 * - the last segment is a scenario name — `nura-first-time` — not a reference,
 *   a token, or anything with entropy in it;
 * - there is no fragment, no query string and no state carried in the URL at
 *   all: the same URL always produces the same synthetic screens;
 * - swapping in a real claim link would require deleting this file rather than
 *   extending it, which is the property that matters.
 *
 * ## What is not here
 *
 * No claim secret, capability, eligibility, expiry, consumption, linkage,
 * routing, backend, persistence, analytics or network call — the
 * `receiver-claim-boundary` gate holds this file to the same rules as the rest
 * of `receiver/`. Recipient values stay memory-only, exactly as before.
 */

/** One offered journey. Nothing here is read from the URL; the URL selects it. */
export interface DemoScenario {
  /** The path segment, and the only thing that distinguishes one link from another. */
  readonly slug: string;
  /** Shown on the index page, never on the claimant surface. */
  readonly label: string;
  readonly tenantId: string;
  readonly scenario: ReceiverScenario;
}

/**
 * The closed set of demo journeys.
 *
 * Five, fixed, and each one registered as its own literal route. Adding a sixth
 * is a code change in two places by design: a scenario that could be conjured
 * by typing a URL would be a parser, and a parser is the thing this milestone
 * must not have.
 */
export const DEMO_SCENARIOS: readonly DemoScenario[] = [
  {
    slug: "nura-first-time",
    label: "Nura — first-time recipient, claim completes",
    tenantId: "nura",
    scenario: { recipient: "first-time", outcome: "completes" },
  },
  {
    slug: "nura-returning",
    label: "Nura — returning recipient, claim completes",
    tenantId: "nura",
    scenario: { recipient: "returning", outcome: "completes" },
  },
  {
    slug: "qasr-first-time",
    label: "Qasr Bank — first-time recipient, three required fields",
    tenantId: "qasr",
    scenario: { recipient: "first-time", outcome: "completes" },
  },
  {
    slug: "lumen-first-time",
    label: "Lumen Sparbank — first-time recipient, full white label",
    tenantId: "lumen",
    scenario: { recipient: "first-time", outcome: "completes" },
  },
  {
    slug: "nura-returning-unavailable",
    label: "Nura — returning recipient, claim cannot complete",
    tenantId: "nura",
    scenario: { recipient: "returning", outcome: "unavailable" },
  },
];

/** The index page's own path, and the prefix every demo link sits under. */
export const DEMO_INDEX_PATH = "/receiver-demo";

/** The full path for a scenario. Used to build the route table, never to parse one. */
export function demoPath(scenario: DemoScenario): string {
  return `${DEMO_INDEX_PATH}/${scenario.slug}`;
}

/**
 * The claimant surface, as a whole page.
 *
 * Rendered with the application chrome suppressed — see `chrome: "bare"` in the
 * route table. That is not decoration: the shell carries Nura's wordmark, a
 * demonstration banner and a footer, and rendering a fully white-labelled bank
 * surface underneath a Nura app bar would put Nura branding on the one screen
 * that must not have any. The surface's own standing disclosure — "Demo only —
 * no money moves and details are not sent or stored" — travels with it and is
 * what keeps the page honest.
 */
export function receiverDemoRoute(
  state: ShellState,
  demo: DemoScenario,
  navigate: Navigate,
): RouteView {
  // The link chooses the institution, so the surface renders as that bank's.
  state.setTenant(tenantById(demo.tenantId));

  const flow = new ClaimFlow(
    new SyntheticClaimProvider({ scenario: demo.scenario, tenant: state.tenant }),
  );

  const surface = claimSurface({
    state,
    flow,
    onFinish: () => {
      // There is no host application to hand back to. The index is the honest
      // destination: it is unmistakably internal, and it is where the next link
      // is picked from.
      navigate(DEMO_INDEX_PATH);
    },
  });

  return {
    node: el("div", { class: "nura-demo-page" }, [surface.root]),
    destroy(): void {
      surface.destroy();
    },
  };
}

/** One link on the index. An ordinary anchor, so a phone can copy it. */
function demoLink(demo: DemoScenario): HTMLElement {
  return el("li", {}, [
    el("a", { class: "nura-demo-link", attrs: { href: demoPath(demo) } }, [
      el("span", { class: "nura-demo-link__label", text: demo.label }),
      el("span", { class: "nura-demo-link__path nura-mono", text: demoPath(demo) }),
      el("span", { class: "nura-demo-link__go", staticHtml: icon("arrow-right", 16) }),
    ]),
  ]);
}

/**
 * The index of demo links.
 *
 * Deliberately plain. Its job is to be a place to copy a link from — long-press
 * in Safari gives the absolute LAN URL — and to say, before anyone taps one,
 * exactly what these links are and are not.
 */
export function receiverDemoIndexRoute(_state: ShellState): RouteView {
  const node = el("div", { class: "nura-stack" }, [
    el("div", { class: "nura-page-header" }, [
      el("p", { class: "nura-eyebrow", text: "Internal reference" }),
      el("h1", { class: "nura-page-header__title", text: "Direct-entry demo links" }),
      el("p", {
        class: "nura-lede",
        text: "Five synthetic journeys, each at its own fixed link, so the receiver experience can be reached the way it eventually will be — by tapping a link in a conversation — instead of by pressing a control on this page.",
      }),
    ]),

    notice({
      tone: "attention",
      title: "These are not claim links",
      text: "Each link names a scenario, not a payment. There is no claim reference, no secret, no fragment and no query string; nothing in the address is read as data, and the same link always opens the same synthetic screens. No production claim URL, route or hosting model is proposed by any of this.",
    }),

    card(
      {
        title: "Open a journey",
        note: "Long-press a link to copy its full address for a phone.",
        headingLevel: "h2",
      },
      [el("ul", { class: "nura-demo-links" }, DEMO_SCENARIOS.map(demoLink))],
    ),

    card({ title: "What stays true on these pages", headingLevel: "h2" }, [
      el("ul", { class: "nura-claim-boundaries" }, [
        el("li", { class: "nura-claim-boundaries__item" }, [
          el("span", { class: "nura-claim-boundaries__marker", staticHtml: icon("check", 14) }),
          el("span", {
            text: "Everything on screen is synthetic and deterministic, and the amount is fixed.",
          }),
        ]),
        el("li", { class: "nura-claim-boundaries__item" }, [
          el("span", { class: "nura-claim-boundaries__marker", staticHtml: icon("check", 14) }),
          el("span", {
            text: "Recipient details stay in memory for one screen: never sent, stored, logged or kept.",
          }),
        ]),
        el("li", { class: "nura-claim-boundaries__item" }, [
          el("span", { class: "nura-claim-boundaries__marker", staticHtml: icon("check", 14) }),
          el("span", {
            text: "No network request, no storage, no analytics, and no claim mechanics of any kind.",
          }),
        ]),
      ]),
    ]),
  ]);

  return { node };
}
